import React, { useEffect, useMemo, useRef } from "react";

import { isValidPostCodeFormat, useGetRegionsByCountry, useTranslation } from "utils";

import { COUNTRY_CODE } from "consts";

import { Address } from "models";

import { Flex, Form, Input, OptGroup, Option, Select } from "components";

export type AddressFormFieldName = "addressStreet" | "addressNumber" | "addressCity" | "zip";

export const AddressForm = ({
  dataTestId,
  address,
  countryCode,
  hiddenFields,
}: {
  dataTestId?: string;
  address: Address | null;
  countryCode: COUNTRY_CODE;
  hiddenFields?: AddressFormFieldName[];
}) => {
  const { t } = useTranslation(["onboarding", "enums"]);

  const form = Form.useFormInstance();

  const { data: regionsData = { isGrouped: false, regions: [] } } = useGetRegionsByCountry({ countryCode });

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    form.setFieldValue("region", undefined);
  }, [countryCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const regionOptions = useMemo(() => {
    if (regionsData.isGrouped) {
      return regionsData.groupedRegions.map((group) => (
        <OptGroup key={`group-${group.country}`} label={group.country}>
          {group.regions.map((region, regionIndex) => (
            <Option
              data-testid={`${dataTestId}-region-select-option-${group.country.toLowerCase().replaceAll(/\s+/g, "-")}-${regionIndex}`}
              value={region}
              key={`${group.country}-${region}`}
            >
              {region}
            </Option>
          ))}
        </OptGroup>
      ));
    }

    return regionsData.regions.map((item) => (
      <Option data-testid={`${dataTestId}-region-select-option-${item}`} value={item} key={item}>
        {item}
      </Option>
    ));
  }, [regionsData, dataTestId]);

  return (
    <Form.Item data-testid={`${dataTestId}-address-form`} name="address">
      <Flex vertical gap={16}>
        <Flex gap={16}>
          <Input
            className={`flex-2 ${hiddenFields?.includes("addressStreet") ? "hidden" : ""}`}
            dataTestId={`${dataTestId}-address-street-input`}
            name="addressStreet"
            label={t("farmDetails.addressStreet")}
            rules={[{ required: true, message: t("farmDetails.errorMessage.streetMissing") }]}
            initialValue={address?.addressStreet ?? undefined}
          />
          <Input
            className={`flex-1 ${hiddenFields?.includes("addressNumber") ? "hidden" : ""}`}
            dataTestId={`${dataTestId}-address-number-input`}
            name="addressNumber"
            label={t("farmDetails.addressNumber")}
            rules={[{ required: true, message: t("farmDetails.errorMessage.streetNumberMissing") }]}
            initialValue={address?.addressNumber ?? undefined}
          />
        </Flex>
        <Flex gap={16}>
          <Input
            className={`flex-2 ${hiddenFields?.includes("addressCity") ? "hidden" : ""}`}
            dataTestId={`${dataTestId}-address-city-input`}
            name="addressCity"
            label={t("farmDetails.addressCity")}
            validateTrigger="onBlur"
            rules={[{ required: true, message: t("farmDetails.errorMessage.cityMissing") }]}
            initialValue={address?.addressCity ?? undefined}
          />
          <Input
            className={`flex-1 ${hiddenFields?.includes("zip") ? "hidden" : ""}`}
            dataTestId={`${dataTestId}-address-zip-input`}
            name="zip"
            initialValue={address?.zip ?? undefined}
            label={t("farmDetails.zip")}
            validateTrigger="onBlur"
            rules={[
              { required: true, message: t("farmDetails.errorMessage.zipMissing") },
              {
                validator: (_: unknown, value: string) => {
                  if (isValidPostCodeFormat(value, countryCode)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("farmDetails.errorMessage.zip")));
                },
              },
            ]}
          />
        </Flex>
        <Select
          dataTestId={`${dataTestId}-address-region-select`}
          name="region"
          label={t("farmDetails.region")}
          showSearch
          filterOption={(input, option) => (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())}
          popupMatchSelectWidth={false}
          rules={[{ required: true, message: t("farmDetails.errorMessage.regionMissing") }]}
          initialValue={address?.region}
        >
          {regionOptions}
        </Select>
      </Flex>
    </Form.Item>
  );
};
